import React, { useEffect, useState } from "react";
import { ArrowRight, Download, Github, Linkedin, ChevronDown } from "lucide-react";
import { Button } from "./ui/button";
import { personalInfo } from "../mock";
import Reveal from "./Reveal";

const rotatingRoles = [
  "AI/ML Engineer",
  "Agentic AI Developer",
  "Intelligent Automation Builder",
];

const techChips = [
  { label: "AI", position: "top-0 left-1/2 -translate-x-1/2 -translate-y-1/2", color: "from-teal-400 to-emerald-500" },
  { label: "ML", position: "right-0 top-1/2 translate-x-1/2 -translate-y-1/2", color: "from-emerald-500 to-teal-500" },
  { label: "Agentic AI", position: "bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2", color: "from-teal-500 to-emerald-600" },
  { label: "Automation", position: "left-0 top-1/2 -translate-x-1/2 -translate-y-1/2", color: "from-emerald-400 to-teal-500" },
];

const Hero = () => {
  const [roleIndex, setRoleIndex] = useState(0);
  const [showRole, setShowRole] = useState(true);

  useEffect(() => {
    const interval = setInterval(() => {
      setShowRole(false);
      setTimeout(() => {
        setRoleIndex((prev) => (prev + 1) % rotatingRoles.length);
        setShowRole(true);
      }, 350);
    }, 3200);
    return () => clearInterval(interval);
  }, []);

  const firstName = personalInfo.name.split(" ")[0];

  return (
    <section
      id="home"
      className="relative flex min-h-[calc(100vh-3rem)] md:min-h-[calc(100vh-3.5rem)] items-center overflow-hidden pt-14 md:pt-16"
    >
      {/* Backdrop layers */}
      <div className="absolute inset-0 bg-grid pointer-events-none" />
      <div className="orb w-[520px] h-[520px] -top-40 -left-32 bg-teal-500/40" />
      <div className="orb w-[460px] h-[460px] top-1/3 -right-40 bg-emerald-500/30" />
      <div className="orb w-[400px] h-[400px] bottom-0 left-1/3 bg-teal-400/30" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full py-8 md:py-10">
        <div className="grid lg:grid-cols-[1.1fr_0.9fr] gap-8 lg:gap-6 items-center">
          {/* Left */}
          <div className="order-2 lg:order-1 text-center lg:text-left space-y-5 md:space-y-6">
            <Reveal delay={80}>
              <p className="font-mono text-[13px] md:text-sm text-primary tracking-wide">
                Hi, my name is
              </p>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mt-1.5 text-foreground">
                {personalInfo.name}
              </h1>
              <h2 className="font-display text-xl sm:text-2xl md:text-3xl lg:text-4xl font-semibold mt-2 min-h-[2rem] text-muted-foreground">
                and I&apos;m a{" "}
                <span
                  key={roleIndex}
                  className={`text-gradient font-bold ${showRole ? "type-fade" : "opacity-0"}`}
                >
                  {rotatingRoles[roleIndex]}
                </span>
              </h2>
            </Reveal>

            <Reveal delay={160}>
              <p className="text-sm md:text-base leading-relaxed text-muted-foreground max-w-xl mx-auto lg:mx-0">
                {personalInfo.tagline}
              </p>
            </Reveal>

            <Reveal delay={240}>
              <div className="flex flex-col sm:flex-row gap-3 justify-center lg:justify-start">
                <Button
                  size="lg"
                  className="cursor-target group rounded-xl bg-gradient-to-r from-teal-600 to-emerald-600 hover:from-teal-500 hover:to-emerald-500 text-white shadow-lg shadow-teal-600/30 h-11 px-6"
                  onClick={() =>
                    document.getElementById("projects").scrollIntoView({ behavior: "smooth" })
                  }
                >
                  View My Work
                  <ArrowRight
                    size={17}
                    className="transition-transform duration-300 group-hover:translate-x-1"
                  />
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  className="cursor-target rounded-xl h-11 px-6 border-border bg-background/40 hover:bg-secondary group"
                  onClick={() => window.open(personalInfo.resume, "_blank")}
                >
                  <Download
                    size={17}
                    className="mr-2 text-primary group-hover:translate-y-0.5 transition-transform"
                  />
                  Download CV
                </Button>
              </div>
            </Reveal>

            <Reveal delay={320}>
              <div className="flex items-center justify-center lg:justify-start gap-3 pt-1">
                <span className="text-muted-foreground text-sm">Follow me</span>
                <span className="hidden sm:block h-px w-10 bg-border" />
                <div className="flex gap-3">
                  {[
                    { icon: Github, href: personalInfo.github, label: "GitHub" },
                    { icon: Linkedin, href: personalInfo.linkedin, label: "LinkedIn" },
                  ].map((social) => (
                    <a
                      key={social.label}
                      href={social.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="cursor-target group border border-border hover:border-primary/60 text-muted-foreground hover:text-primary p-2.5 rounded-lg transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-primary/10"
                      aria-label={social.label}
                    >
                      <social.icon size={22} />
                    </a>
                  ))}
                </div>
              </div>
            </Reveal>
          </div>

          {/* Right — profile */}
          <Reveal delay={200} className="order-1 lg:order-2 flex justify-center lg:justify-end lg:pr-24">
            <div className="relative w-64 h-64 sm:w-80 sm:h-80 lg:w-96 lg:h-96">
              {/* Rotating gradient ring */}
              <div className="absolute -inset-3 rounded-full bg-gradient-to-tr from-teal-400 via-emerald-400 to-teal-500 opacity-60 blur-2xl animate-pulse" />

              <div className="relative w-full h-full">
                <div className="absolute inset-0 rounded-full border border-primary/30" />
                <div className="absolute -inset-2 md:-inset-3 rounded-full border border-dashed border-primary/20 animate-spin-slow" />

                {/* Photo frame */}
                <div className="absolute inset-0 p-8 rounded-full overflow-hidden glass">
                  <img
                    src={personalInfo.profileImage}
                    alt={personalInfo.name}
                    className="w-full h-full object-cover rounded-full"
                  />
                </div>

                {/* Revolving tech chips */}
                <div className="absolute -inset-8 md:-inset-10 animate-spin-slow">
                  {techChips.map((chip) => (
                    <div
                      key={chip.label}
                      className={`absolute ${chip.position} animate-spin-reverse`}
                    >
                      <div className="px-2.5 py-1 rounded-lg glass text-[11px] md:text-xs font-semibold flex items-center gap-1.5 shadow-lg whitespace-nowrap">
                        <span
                          className={`h-1.5 w-1.5 rounded-full bg-gradient-to-r ${chip.color}`}
                        />
                        {chip.label}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </div>

      {/* Scroll cue */}
      <a
        href="#about"
        className="cursor-target absolute bottom-5 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1 text-muted-foreground hover:text-primary transition-colors"
        aria-label="Scroll to about"
      >
        <span className="font-mono text-[10px] uppercase tracking-[0.25em]">Scroll</span>
        <ChevronDown size={16} className="animate-bounce" />
      </a>
    </section>
  );
};

export default Hero;